import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { handleGetPublicPitches } from "../../shared/controllers/pitchController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import LoadingGrid from "../../utils/loadingGrid";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface Pitch {
    id: string | number;
    name: string;
    location: string;
    image?: string;
}

const Pitches: React.FC = () => {
    const [pitches, setPitches] = useState<Pitch[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPitches = async () => {
            try {
                const response = await handleGetPublicPitches();
                setPitches(unwrapApiArray<Pitch>(response));
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchPitches();
    }, []);

    return (
        <>
            <Navbar />
            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 text-center flex flex-col items-center justify-center p-4"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight">World-Class Pitches</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
                        From professional stadiums to community grounds. Discover and book high-quality venues for your next kickabout.
                    </p>
                </div>
            </div>

            <div className="py-24 bg-white px-4 md:px-14">
                <div className="max-w-7xl mx-auto">
                    <div className="space-y-4 mb-20 text-center max-w-2xl mx-auto">
                        <h3 className="text-green-700 font-black uppercase tracking-[0.25em] text-sm">Premier Venues</h3>
                        <h2 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter">Choose Your Stadium</h2>
                        <p className="text-gray-500 font-bold italic text-lg">Every great game starts with a great pitch. Select from our curated list of professional venues.</p>
                    </div>

                    {loading ? (
                        <LoadingGrid count={6} />
                    ) : pitches.length === 0 ? (
                        <div className="bg-gray-50 py-32 rounded-[64px] text-center border-2 border-dashed border-gray-100 italic font-bold text-gray-400 text-2xl">
                            No pitches are currently available for booking.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                            {pitches.map((pitch, i) => (
                                <div key={i} className="group flex flex-col items-center text-center space-y-8 font-Manrope transition-all duration-500">
                                    <div className="w-full aspect-square bg-gray-100 rounded-[64px] overflow-hidden relative shadow-xl hover:shadow-2xl hover:-rotate-2 transition-all duration-500">
                                        <img src={pitch.image || landingAsset("stadia.svg")} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="text-3xl font-black text-gray-900 group-hover:text-green-700 transition-colors uppercase tracking-tight">{pitch.name}</h3>
                                        <p className="text-gray-500 font-bold flex items-center justify-center gap-2 text-lg">📍 {pitch.location || "Multiple Locations"}</p>
                                        <div className="pt-4">
                                            <Link to={`/pitches/${pitch.id}`} className="px-12 py-4 bg-gray-900 text-white rounded-2xl font-black text-lg hover:bg-green-700 transition-all active:scale-95 shadow-lg">Book Pitch</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default Pitches;
